import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/", { replace: true });
  };

  const closeMobile = () => setMobileOpen(false);

  const desktopLinkCls = ({ isActive }) =>
    `relative text-sm transition-colors ${
      isActive
        ? "text-neutral-900 after:absolute after:-bottom-[22px] after:left-0 after:h-[2px] after:w-full after:bg-coral"
        : "text-neutral-500 hover:text-neutral-900"
    }`;
  const mobileLinkCls = ({ isActive }) =>
    `block border-b border-neutral-100 py-4 font-serif text-2xl ${
      isActive ? "text-black" : "text-neutral-500"
    }`;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-neutral-200 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-10">
        <div onClick={closeMobile}>
          <Logo />
        </div>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 md:flex">
          {user && (
            <NavLink to="/dashboard" data-testid="nav-dashboard" className={desktopLinkCls}>
              Studio
            </NavLink>
          )}
          {user && (
            <NavLink to="/ideas" data-testid="nav-ideas" className={desktopLinkCls}>
              Ideas
            </NavLink>
          )}
          {user && (
            <NavLink to="/history" data-testid="nav-history" className={desktopLinkCls}>
              History
            </NavLink>
          )}
          {user && (
            <NavLink to="/brand-voice" data-testid="nav-brand-voice" className={desktopLinkCls}>
              Voice
            </NavLink>
          )}
          {user && (
            <NavLink to="/templates" data-testid="nav-templates" className={desktopLinkCls}>
              Templates
            </NavLink>
          )}
          {user?.is_admin && (
            <NavLink to="/admin" data-testid="nav-admin" className={desktopLinkCls}>
              Admin
            </NavLink>
          )}
          <NavLink to="/pricing" data-testid="nav-pricing" className={desktopLinkCls}>
            Pricing
          </NavLink>
        </nav>

        {/* Desktop right actions */}
        <div className="hidden items-center gap-3 md:flex">
          {user ? (
            <>
              <NavLink
                to="/settings"
                data-testid="nav-settings"
                className={({ isActive }) =>
                  `text-xs transition-colors ${isActive ? "text-neutral-900" : "text-neutral-500 hover:text-neutral-900"}`
                }
              >
                {user.email}
              </NavLink>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="rounded-[3px] border-neutral-300 text-xs"
              >
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm text-neutral-600 hover:text-neutral-900">
                Sign in
              </Link>
              <Link to="/signup" data-testid="nav-signup">
                <Button
                  size="sm"
                  className="rounded-[3px] bg-coral text-white shadow-none hover:bg-coral-hover hover:shadow-coral"
                >
                  Start free
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="nav-mobile-menu"
          className="rounded-none border border-neutral-300 p-2 md:hidden"
        >
          {mobileOpen ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div
          id="nav-mobile-menu"
          data-testid="nav-mobile-menu"
          className="border-t border-neutral-200 bg-white px-6 pb-8 pt-2 md:hidden"
        >
          {user && (
            <>
              <NavLink
                to="/dashboard"
                data-testid="mobile-nav-dashboard"
                onClick={closeMobile}
                className={mobileLinkCls}
              >
                Studio
              </NavLink>
              <NavLink
                to="/ideas"
                data-testid="mobile-nav-ideas"
                onClick={closeMobile}
                className={mobileLinkCls}
              >
                Ideas
              </NavLink>
              <NavLink
                to="/history"
                data-testid="mobile-nav-history"
                onClick={closeMobile}
                className={mobileLinkCls}
              >
                History
              </NavLink>
              <NavLink
                to="/brand-voice"
                data-testid="mobile-nav-brand-voice"
                onClick={closeMobile}
                className={mobileLinkCls}
              >
                Voice
              </NavLink>
              <NavLink
                to="/templates"
                data-testid="mobile-nav-templates"
                onClick={closeMobile}
                className={mobileLinkCls}
              >
                Templates
              </NavLink>
              {user?.is_admin && (
                <NavLink
                  to="/admin"
                  data-testid="mobile-nav-admin"
                  onClick={closeMobile}
                  className={mobileLinkCls}
                >
                  Admin
                </NavLink>
              )}
              <NavLink
                to="/settings"
                data-testid="mobile-nav-settings"
                onClick={closeMobile}
                className={mobileLinkCls}
              >
                Settings
              </NavLink>
            </>
          )}
          <NavLink
            to="/pricing"
            data-testid="mobile-nav-pricing"
            onClick={closeMobile}
            className={mobileLinkCls}
          >
            Pricing
          </NavLink>

          <div className="mt-6">
            {user ? (
              <>
                <p className="mb-3 truncate text-xs uppercase tracking-widest text-neutral-500">
                  {user.email}
                </p>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  data-testid="mobile-nav-logout-btn"
                  className="h-11 w-full rounded-[3px] border-neutral-300"
                >
                  Sign out
                </Button>
              </>
            ) : (
              <div className="flex flex-col gap-3">
                <Link to="/login" data-testid="mobile-nav-login" onClick={closeMobile}>
                  <Button variant="outline" className="h-11 w-full rounded-[3px] border-neutral-300">
                    Sign in
                  </Button>
                </Link>
                <Link to="/signup" data-testid="mobile-nav-signup" onClick={closeMobile}>
                  <Button className="h-11 w-full rounded-[3px] bg-coral text-white hover:bg-coral-hover">
                    Start free
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
